import { wrap } from 'svelte-spa-router/wrap';
import { get } from 'svelte/store';
import { push } from 'svelte-spa-router';
import Login from "../framework/components/Login.svelte";
import Signup from "../framework/components/Signup.svelte";
import Profile from "../framework/components/Profile.svelte";
import UserList from "../framework/components/UserList.svelte";
import OrderList from "../framework/components/OrderList.svelte";
import ProductList from "../framework/components/ProductList.svelte";
import BriefQuestionList from "../components/BriefQuestionList.svelte";
import Shop from "../framework/components/Shop.svelte";
import ShoppingCart from "../framework/components/ShoppingCart.svelte"
import Checkout from "../framework/components/Checkout.svelte"
import NotFound from "../framework/components/NotFound.svelte";
import PasswordReset from "../framework/components/PasswordReset.svelte";
import SendPasswordReset from "../framework/components/PasswordResetSend.svelte";
import DashboardAdmin from "../components/DashboardAdmin.svelte";
import DashboardUser from "../components/DashboardUser.svelte";
import Brief from "../components/brief/Brief.svelte";
import BriefView from "../components/brief/BriefView.svelte";
import BriefAdd from "../components/brief/BriefAdd.svelte";
import UserHome from "../components/UserHome.svelte";
import BriefHome from "../components/brief/BriefHome.svelte";

import Onboarding from "../framework/components/Onboarding.svelte";
import GuestBriefAdd from "../components/brief/GuestBriefAdd.svelte";
import GuestBriefSelect from "../components/brief/GuestBriefSelect.svelte";
import GuestIlluminator from "../components/brief/GuestIlluminator.svelte";
import GuestGapfinder from "../components/brief/GuestGapfinder.svelte";
import GuestBrief from "../components/brief/GuestBrief.svelte";
import GuestBriefBuilder from "../components/brief-builder/GuestBriefBuilder.svelte";
import BrandSentiment from "../components/BrandSentiment.svelte";
import TeamInviteAccept from "../components/team/TeamInviteAccept.svelte";

import { auth } from "../framework/js/auth.js";

// Routes that don't require authentication
export const publicPaths = new Set([
  '/', '/home', '/login', '/signup',
  '/reset-password',
  '/guest-brief-new',
  '/guest-brief-new-btob',
  '/guest-brief-new-illuminator',
  '/guest-brief-new-gapfinder',
  '/guest-brief-update',
  '/guest-brief-builder',
]);

// Route prefixes that are public (for dynamic segments)
export const publicPrefixes = ['/reset-password/', '/team-invite/', '/guest-brief'];

/** @param {string} path */
export function isPublicRoute(path) {
  if (publicPaths.has(path)) return true;
  return publicPrefixes.some(prefix => path.startsWith(prefix));
}

function rootRedirect() {
  if (get(auth)?.token) {
    push(auth.userIsAdmin() ? '/dashboard-admin' : '/dashboard');
  } else {
    push('/login');
  }
  return false;
}

/**
 * Resume in-progress brief wizard (legacy /brief/:id URLs without /continue).
 * @param {{ params?: Record<string, string> | null }} detail
 */
function briefResumeRedirect(detail) {
  const id = detail?.params?.id;
  if (id) {
    push(`/brief/${id}/continue`);
  } else {
    push('/briefs');
  }
  return false;
}

const RootRedirect = wrap({
  component: NotFound,
  conditions: [rootRedirect],
});

const BriefResumeRedirect = wrap({
  component: DashboardUser,
  conditions: [briefResumeRedirect],
});

export const routes = {
  '/': RootRedirect,
  '/home': RootRedirect,
  '/login': Login,
  '/signup': Signup,
  '/onboarding': Onboarding,
  '/user-maintenance': UserList,
  '/product-maintenance': ProductList,
  '/x-brief-question-maintenance': BriefQuestionList,
  '/reset-password': SendPasswordReset,
  '/reset-password/:token': PasswordReset,
  '/shop': Shop,
  '/shopping-cart': ShoppingCart,
  '/checkout': Checkout,
  '/user-order-history': OrderList,

  // Admin Dashboard Routes
  '/dashboard-admin': DashboardAdmin,
  '/admin/users': DashboardAdmin,
  '/admin/users/:uuid': DashboardAdmin,
  '/admin/briefs': DashboardAdmin,
  '/admin/briefs/:id': DashboardAdmin,
  '/admin/brief-questions': DashboardAdmin,
  '/admin/proposal-requests': DashboardAdmin,
  '/admin/invoices': DashboardAdmin,
  '/admin/invoices/:uuid': DashboardAdmin,
  '/admin/activity-log': DashboardAdmin,
  '/admin/ai-processes': DashboardAdmin,
  '/admin/teams': DashboardAdmin,
  '/admin/teams/:uuid': DashboardAdmin,
  '/admin/billing': DashboardAdmin,
  '/admin/credit-usage': DashboardAdmin,
  '/admin/knowledge/platform': DashboardAdmin,
  '/admin/knowledge/brief-types': DashboardAdmin,
  '/admin/knowledge/brief-types/:uuid': DashboardAdmin,
  '/admin/knowledge/test': DashboardAdmin,

  // User Dashboard Routes
  '/dashboard': DashboardUser,
  '/brief-builder': DashboardUser,
  '/brief-builder/new': DashboardUser,
  '/brief-builder/:id': DashboardUser,
  '/brief-builder/:id/continue': DashboardUser,
  '/briefs': DashboardUser,
  '/briefs/:id': DashboardUser,
  '/assessments': DashboardUser,
  '/assessments/new': DashboardUser,
  '/assessments/:id/edit': DashboardUser,
  '/assessments/:id': DashboardUser,
  '/gapfinder': DashboardUser,
  '/gapfinder/new': DashboardUser,
  '/gapfinder/history': DashboardUser,
  '/gapfinder/:id': DashboardUser,
  '/team-knowledge': DashboardUser,
  '/research-library': DashboardUser,
  '/ai-processes': DashboardUser,
  '/learning': DashboardUser,
  '/profile': DashboardUser,
  '/brief/:id': BriefResumeRedirect,
  '/brief/:id/edit': DashboardUser,
  '/brief/:id/continue': DashboardUser,
  '/brief-new': DashboardUser,
  '/guest-brief-new': GuestBriefSelect,
  '/guest-brief-new-btob': GuestBriefAdd,
  '/guest-brief-builder': GuestBriefBuilder,
  '/guest-brief-new-illuminator': GuestIlluminator,
  '/guest-brief-new-gapfinder': GuestGapfinder,
  '/guest-brief-update': GuestBrief,
  '/team-invite/:token': TeamInviteAccept,
  '/team': DashboardUser,
  '/payment-plans': DashboardUser,
  '/payment-plan': DashboardUser,
  '/invoices/:uuid': DashboardUser,
  '/invoices': DashboardUser,
  '*': NotFound
};
